// src/components/sponsorships/SponsorDetails.jsx
// ─────────────────────────────────────────────────────────────────────────────
//  Sponsor Details — "TelcoFair Glass Workspace" with Enhanced Animations
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Container,
    Paper,
    Typography,
    Grid,
    Card,
    CardContent,
    Avatar,
    Button,
    Chip,
    Divider,
    LinearProgress,
    IconButton,
    Tooltip,
    Stack,
    Alert,
    Tab,
    Tabs,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Skeleton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Switch,
    FormControlLabel,
    CircularProgress,
    TablePagination,
    Snackbar,
    InputAdornment,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    Schedule as ScheduleIcon,
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
    People as PeopleIcon,
    Visibility as VisibilityIcon,
    Analytics as AnalyticsIcon,
    Storefront as StorefrontIcon,
    Money as MoneyIcon,
    CreditCard as CreditCardIcon,
    CalendarToday as CalendarIcon,
    LocationOn as LocationIcon,
    Business as BusinessIcon,
    Email as EmailIcon,
    Phone as PhoneIcon,
    Language as LanguageIcon,
    Download as DownloadIcon,
    Refresh as RefreshIcon,
    Save as SaveIcon,
    Close as CloseIcon,
    Add as AddIcon,
    Image as ImageIcon,
    Videocam as VideocamIcon,
    Slideshow as SlideshowIcon,
    Link as LinkIcon,
    Public as PublicIcon,
    Assessment as AssessmentIcon,
    Timer as TimerIcon,
    Speed as SpeedIcon,
    Verified as VerifiedIcon,
    Warning as WarningIcon,
    FiberManualRecord as LiveIcon,
    Star as StarIcon,
    AutoAwesome as AutoAwesomeIcon,
    Diamond as DiamondIcon,
} from '@mui/icons-material';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../config/firebase';
import {
    getDoc,
    doc,
    updateDoc,
    deleteDoc,
    serverTimestamp,
    collection,
    getDocs,
    query,
    where,
    orderBy,
    addDoc,
    onSnapshot
} from 'firebase/firestore';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';

// ─────────────────────────────────────────────────────────────────────────────
// 1 · DESIGN TOKENS — "TelcoFair Glass Workspace"
// ─────────────────────────────────────────────────────────────────────────────

const palette = {
    canvas: '#F2F7F9',
    canvasAlt: '#F7FAFC',
    paper: 'rgba(255,255,255,0.88)',
    paperSolid: '#FFFFFF',
    ink: '#182235',
    inkSoft: '#506175',
    inkLight: '#7B8B9C',
    inkMuted: '#AAB6C1',
    hairline: 'rgba(31,62,82,0.09)',
    hairlineStrong: 'rgba(31,62,82,0.14)',
    rust: 'var(--primary, #2E6F8E)',
    rustDeep: 'var(--secondary, #42B89B)',
    pine: '#2E8B69',
    pineSoft: '#4CA17F',
    gold: '#D78B28',
    goldLight: '#E2A54D',
    teal: '#268AA3',
    tealLight: '#5EADC0',
    purple: '#8D5AC8',
    purpleLight: '#A97AD9',
    navy: '#315E9E',
    success: '#10B981',
    warning: '#F59E0B',
    danger: '#EF4444',
    info: '#3B82F6',
    admin: '#7557C7',
    clay: '#B85E4B',
    coral: '#D96F68',
    sage: '#6FA58C',
    lavender: '#9878BF',
    honey: '#E5BC68',
    ocean: '#4A8B9B',
};

const rgba = (color, a = 1) => {
    if (typeof color === 'string' && color.startsWith('var(')) return `color-mix(in srgb, ${color} ${Math.max(0, Math.min(100, Number(a) * 100))}%, transparent)`;
    if (typeof color === 'string' && color.startsWith('rgb')) return color;
    const hex = color.replace('#', '');
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${a})`;
};

const fontDisplay = "'Plus Jakarta Sans', 'Inter', sans-serif";
const fontBody = "'Plus Jakarta Sans', 'Inter', sans-serif";
const fontMono = "'JetBrains Mono', 'SFMono-Regular', monospace";

const FONT_IMPORT = `@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700&display=swap');`;

// ─────────────────────────────────────────────────────────────────────────────
// 2 · LIQUID GLASS FUNCTIONS
// ─────────────────────────────────────────────────────────────────────────────

const liquidGlass = (accent = palette.rust, intensity = 0.12, elevation = 'md') => {
    const elevations = {
        xs: `0 1px 2px ${rgba(palette.ink, 0.04)}, 0 1px 4px ${rgba(palette.ink, 0.04)}`,
        sm: `0 2px 4px ${rgba(palette.ink, 0.06)}, 0 4px 12px ${rgba(palette.ink, 0.08)}`,
        md: `0 4px 8px ${rgba(palette.ink, 0.08)}, 0 8px 20px ${rgba(palette.ink, 0.10)}`,
        lg: `0 8px 16px ${rgba(palette.ink, 0.10)}, 0 16px 40px ${rgba(palette.ink, 0.12)}`,
        xl: `0 16px 32px ${rgba(palette.ink, 0.12)}, 0 24px 64px ${rgba(palette.ink, 0.15)}`,
    };

    return {
        background: `linear-gradient(155deg, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.50) 42%, ${rgba(accent, intensity)} 100%)`,
        backdropFilter: 'blur(24px) saturate(200%)',
        WebkitBackdropFilter: 'blur(24px) saturate(200%)',
        border: `1px solid rgba(255,255,255,0.6)`,
        boxShadow: [
            `inset 0 1px 0 rgba(255,255,255,0.95)`,
            `inset 0 -1px 0 rgba(255,255,255,0.30)`,
            `inset 0 0 0 1px ${rgba(accent, 0.04)}`,
            elevations[elevation] || elevations.md,
            `0 12px 40px -8px ${rgba(accent, 0.15)}`,
        ].join(', '),
        overflow: 'hidden',
        transition: 'box-shadow .6s cubic-bezier(.22,1,.36,1), transform .6s cubic-bezier(.22,1,.36,1), border-color .5s ease',
    };
};

const sheenLayer = (accent) => ({
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    opacity: 'var(--sheen, 0)',
    transition: 'opacity .5s ease',
    background: `radial-gradient(420px circle at var(--mx, 50%) var(--my, 0%), rgba(255,255,255,0.7), ${rgba(accent, 0.10)} 38%, transparent 68%)`,
    mixBlendMode: 'soft-light',
    content: '""',
    borderRadius: 'inherit',
});

const useSheen = () => {
    const ref = useRef(null);
    const onPointerMove = (e) => {
        const el = ref.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        el.style.setProperty('--mx', `${((e.clientX - r.left) / r.width) * 100}%`);
        el.style.setProperty('--my', `${((e.clientY - r.top) / r.height) * 100}%`);
        el.style.setProperty('--sheen', '1');
    };
    const onPointerLeave = () => {
        const el = ref.current;
        if (el) el.style.setProperty('--sheen', '0');
    };
    return { ref, onPointerMove, onPointerLeave };
};

// ─────────────────────────────────────────────────────────────────────────────
// 3 · GLASS COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────

const GlassCard = ({ children, accent = palette.rust, sx = {}, delay = 0, hover = true, elevation = 'md', ...props }) => {
    const { ref, onPointerMove, onPointerLeave } = useSheen();
    const [isHovered, setIsHovered] = useState(false);

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] }}
            whileHover={hover ? { y: -6, transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] } } : {}}
            onHoverStart={() => setIsHovered(true)}
            onHoverEnd={() => setIsHovered(false)}
            style={{ height: '100%', width: '100%' }}
        >
            <Paper
                ref={ref}
                onPointerMove={onPointerMove}
                onPointerLeave={onPointerLeave}
                elevation={0}
                sx={{
                    ...liquidGlass(accent, 0.12, elevation),
                    position: 'relative',
                    p: { xs: 2.5, md: 3 },
                    borderRadius: '20px',
                    border: `1.5px solid ${rgba(accent, 0.10)}`,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    boxSizing: 'border-box',
                    '&::after': sheenLayer(accent),
                    transform: isHovered && hover ? 'translateY(-4px) scale(1.003)' : 'translateY(0) scale(1)',
                    boxShadow: isHovered && hover 
                        ? [
                            `inset 0 1px 0 rgba(255,255,255,0.95)`,
                            `inset 0 -1px 0 rgba(255,255,255,0.30)`,
                            `inset 0 0 0 1px ${rgba(accent, 0.08)}`,
                            `0 20px 60px -16px ${rgba(accent, 0.30)}`,
                            `0 8px 24px ${rgba(palette.ink, 0.10)}`,
                        ].join(', ')
                        : liquidGlass(accent, 0.12, elevation).boxShadow,
                    transition: 'all 0.4s cubic-bezier(.22,1,.36,1)',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '3px',
                        background: `linear-gradient(90deg, transparent, ${rgba(accent, 0.4)}, transparent)`,
                        opacity: isHovered && hover ? 1 : 0.4,
                        transition: 'opacity 0.4s ease',
                        pointerEvents: 'none',
                        borderRadius: '20px 20px 0 0',
                        zIndex: 2,
                    },
                    ...sx,
                }}
                {...props}
            >
                {children}
            </Paper>
        </motion.div>
    );
};

const GlassStatCard = ({ title, value, icon, color, gradient, delay, subtitle, change, trend }) => {
    const nodeRef = useRef(null);
    const hasRun = useRef(false);
    const [isHovered, setIsHovered] = useState(false);
    const { ref, onPointerMove, onPointerLeave } = useSheen();

    useEffect(() => {
        if (typeof value === 'number' && value > 0) {
            const node = nodeRef.current;
            if (!node || hasRun.current) return;

            const observer = new IntersectionObserver(
                ([entry]) => {
                    if (entry.isIntersecting && !hasRun.current) {
                        hasRun.current = true;
                        let current = 0;
                        const step = Math.ceil(value / 40);
                        const interval = setInterval(() => {
                            current += step;
                            if (current >= value) {
                                current = value;
                                clearInterval(interval);
                            }
                            if (node) node.textContent = Math.round(current).toLocaleString();
                        }, 25);
                        return () => clearInterval(interval);
                    }
                },
                { threshold: 0.3 }
            );

            observer.observe(node);
            return () => observer.disconnect();
        }
    }, [value]);

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] }}
            whileHover={{ y: -6, transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] } }}
            onHoverStart={() => setIsHovered(true)}
            onHoverEnd={() => setIsHovered(false)}
            style={{ height: '100%' }}
        >
            <Paper
                ref={ref}
                onPointerMove={onPointerMove}
                onPointerLeave={onPointerLeave}
                elevation={0}
                sx={{
                    ...liquidGlass(color, 0.15, 'lg'),
                    position: 'relative',
                    borderRadius: '24px',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    '&::after': sheenLayer(color),
                    transform: isHovered ? 'translateY(-6px) scale(1.005)' : 'translateY(0) scale(1)',
                    boxShadow: isHovered 
                        ? [
                            `inset 0 1px 0 rgba(255,255,255,0.95)`,
                            `inset 0 -1px 0 rgba(255,255,255,0.30)`,
                            `inset 0 0 0 1px ${rgba(color, 0.10)}`,
                            `0 24px 64px -16px ${rgba(color, 0.35)}`,
                            `0 12px 32px ${rgba(palette.ink, 0.12)}`,
                        ].join(', ')
                        : liquidGlass(color, 0.15, 'lg').boxShadow,
                    transition: 'all 0.4s cubic-bezier(.22,1,.36,1)',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '3px',
                        background: `linear-gradient(90deg, transparent, ${rgba(color, 0.5)}, transparent)`,
                        opacity: isHovered ? 1 : 0.4,
                        transition: 'opacity 0.4s ease',
                        pointerEvents: 'none',
                        borderRadius: '24px 24px 0 0',
                        zIndex: 2,
                    },
                }}
            >
                <CardContent sx={{ p: { xs: 2.5, md: 3 }, flex: 1, display: 'flex', flexDirection: 'column', position: 'relative', zIndex: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <motion.div
                            whileHover={{ scale: 1.12, rotate: -5, transition: { type: 'spring', stiffness: 400, damping: 17 } }}
                            style={{
                                width: 56,
                                height: 56,
                                borderRadius: '16px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: gradient || `linear-gradient(135deg, ${color}, ${color}80)`,
                                color: '#fff',
                                boxShadow: `0 4px 20px ${rgba(color, 0.35)}`,
                                position: 'relative',
                                border: `2px solid ${rgba(color, 0.15)}`,
                                willChange: 'transform',
                                backdropFilter: 'blur(4px)',
                            }}
                        >
                            {React.cloneElement(icon, { sx: { fontSize: 24 } })}
                        </motion.div>
                        {change && (
                            <Chip
                                label={change}
                                size="small"
                                sx={{
                                    fontWeight: 700,
                                    fontSize: '0.7rem',
                                    height: 26,
                                    px: 0.75,
                                    bgcolor: trend === 'up' ? rgba(palette.pine, 0.12) : rgba(palette.danger, 0.12),
                                    color: trend === 'up' ? palette.pine : palette.danger,
                                    border: `1.5px solid ${trend === 'up' ? rgba(palette.pine, 0.2) : rgba(palette.danger, 0.2)}`,
                                    backdropFilter: 'blur(4px)',
                                }}
                                icon={trend === 'up' ? <TrendingUpIcon sx={{ fontSize: 14 }} /> : <TrendingDownIcon sx={{ fontSize: 14 }} />}
                            />
                        )}
                    </Box>
                    <Typography
                        sx={{
                            fontFamily: fontDisplay,
                            fontWeight: 700,
                            mb: 0.5,
                            fontSize: { xs: '2rem', md: '2.5rem' },
                            lineHeight: 1,
                            background: `linear-gradient(135deg, ${palette.ink} 30%, ${color})`,
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            display: 'inline-block',
                        }}
                    >
                        <span ref={nodeRef}>0</span>
                    </Typography>
                    <Typography sx={{ color: palette.inkSoft, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: '0.7rem' }}>
                        {title}
                    </Typography>
                    {subtitle && <Typography sx={{ color: palette.inkLight, fontSize: '0.7rem', mt: 0.25 }}>{subtitle}</Typography>}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 'auto', pt: 2 }}>
                        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: palette.success, position: 'relative', zIndex: 1 }} />
                            <motion.div
                                animate={{ scale: [1, 1.5, 1], opacity: [0.3, 0.1, 0.3] }}
                                transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                                style={{ position: 'absolute', width: 16, height: 16, borderRadius: '50%', background: palette.success, opacity: 0.3 }}
                            />
                        </Box>
                        <Typography variant="caption" sx={{ color: palette.success, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.65rem' }}>
                            Live Data
                        </Typography>
                    </Box>
                </CardContent>
            </Paper>
        </motion.div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// 4 · FLOATING BACKGROUND ORBS
// ─────────────────────────────────────────────────────────────────────────────

const FloatingOrbs = () => {
    const orbs = [
        { color: palette.rust, size: 400, top: '-10%', left: '2%', duration: 28, delay: 0 },
        { color: palette.pine, size: 340, top: '15%', left: '85%', duration: 34, delay: 2 },
        { color: palette.gold, size: 280, top: '55%', left: '-8%', duration: 26, delay: 1 },
        { color: palette.purple, size: 320, top: '75%', left: '72%', duration: 32, delay: 3 },
        { color: palette.teal, size: 240, top: '35%', left: '45%', duration: 30, delay: 1.5 },
    ];

    return (
        <Box aria-hidden="true" sx={{ position: 'fixed', inset: 0, overflow: 'hidden', pointerEvents: 'none', zIndex: 0 }}>
            {orbs.map((orb, i) => (
                <motion.div
                    key={i}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: [0.25, 0.5, 0.25], x: [0, 40, -30, 0], y: [0, -35, 25, 0], scale: [1, 1.1, 0.95, 1] }}
                    transition={{ duration: orb.duration, delay: orb.delay, repeat: Infinity, ease: 'easeInOut' }}
                    style={{
                        position: 'absolute',
                        top: orb.top,
                        left: orb.left,
                        width: orb.size,
                        height: orb.size,
                        borderRadius: '50%',
                        background: `radial-gradient(circle, ${orb.color}20, ${orb.color}00 70%)`,
                        filter: 'blur(8px)',
                    }}
                />
            ))}
        </Box>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// 5 · SECTION HEADER
// ─────────────────────────────────────────────────────────────────────────────

const SectionHeader = ({ icon, title, subtitle, color = palette.rust }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Box sx={{ 
            bgcolor: color, 
            color: '#fff',
            width: 40,
            height: 40,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 4px 16px ${rgba(color, 0.3)}`
        }}>
            {icon}
        </Box>
        <Box>
            <Typography variant="h6" sx={{ fontFamily: fontDisplay, fontWeight: 700, color: palette.ink }}>
                {title}
            </Typography>
            <Typography variant="body2" sx={{ color: palette.inkLight }}>
                {subtitle}
            </Typography>
        </Box>
    </Box>
);

// ─────────────────────────────────────────────────────────────────────────────
// 6 · SPONSOR DETAILS COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

const SponsorDetails = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { user } = useAuth();
    
    const [loading, setLoading] = useState(true);
    const [sponsorship, setSponsorship] = useState(null);
    const [company, setCompany] = useState(null);
    const [ads, setAds] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [stats, setStats] = useState({
        totalViews: 0,
        clicks: 0,
        conversions: 0,
        revenue: 0,
        ctr: 0
    });
    const [tabValue, setTabValue] = useState(0);
    const [openAdDialog, setOpenAdDialog] = useState(false);
    const [dialogMode, setDialogMode] = useState('add');
    const [selectedAd, setSelectedAd] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        type: 'image',
        content: '',
        link: '',
        placement: 'sidebar',
        isActive: true,
        impressions: 0,
        clicks: 0
    });
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // Immersive scroll
    const pageRef = useRef(null);
    const { scrollYProgress } = useScroll({ target: pageRef, offset: ['start start', 'end start'] });
    const headerOpacity = useTransform(scrollYProgress, [0, 0.15], [1, 0.85]);

    // Canvas styles
    const canvasSx = {
        minHeight: '100vh',
        position: 'relative',
        bgcolor: palette.canvas,
        fontFamily: fontBody,
        '&::before': {
            content: '""',
            position: 'fixed',
            inset: 0,
            pointerEvents: 'none',
            opacity: 0.3,
            backgroundImage:
                'url("data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%27120%27 height=%27120%27%3E%3Cfilter id=%27n%27%3E%3CfeTurbulence type=%27fractalNoise%27 baseFrequency=%270.9%27 numOctaves=%272%27 stitchTiles=%27stitch%27/%3E%3C/filter%3E%3Crect width=%27100%25%27 height=%27100%25%27 filter=%27url(%23n)%27 opacity=%270.025%27/%3E%3C/svg%3E")',
            animation: 'grainDrift 9s steps(6) infinite',
        },
    };

    // ─────────────────────────────────────────────────────────────────────────
    // DATA FETCHING
    // ─────────────────────────────────────────────────────────────────────────

    useEffect(() => {
        if (id) {
            fetchSponsorshipData();
            fetchAds();

            const unsubscribe = onSnapshot(
                query(collection(db, 'ads'), where('sponsorshipId', '==', id)),
                (snapshot) => {
                    const adsList = [];
                    snapshot.forEach((doc) => {
                        adsList.push({ id: doc.id, ...doc.data() });
                    });
                    setAds(adsList);
                    calculateStats(adsList);
                },
                (error) => {
                    console.warn('Ads listener error:', error);
                }
            );

            return () => unsubscribe();
        }
    }, [id]);

    const fetchSponsorshipData = async () => {
        setLoading(true);
        try {
            const sponsorshipDoc = await getDoc(doc(db, 'sponsorships', id));
            if (sponsorshipDoc.exists()) {
                const data = sponsorshipDoc.data();
                setSponsorship({ id: sponsorshipDoc.id, ...data });

                if (data.companyId) {
                    const companyDoc = await getDoc(doc(db, 'companies', data.companyId));
                    if (companyDoc.exists()) {
                        setCompany({ id: companyDoc.id, ...companyDoc.data() });
                    }
                }
            } else {
                toast.error('Sponsorship not found');
                navigate('/sponsorships');
            }
        } catch (error) {
            console.error('Error fetching sponsorship:', error);
            toast.error('Failed to load sponsorship data');
        } finally {
            setLoading(false);
        }
    };

    const fetchAds = async () => {
        try {
            const adsQuery = query(
                collection(db, 'ads'),
                where('sponsorshipId', '==', id),
                orderBy('createdAt', 'desc')
            );
            const snapshot = await getDocs(adsQuery);
            const adsList = [];
            snapshot.forEach((doc) => {
                adsList.push({ id: doc.id, ...doc.data() });
            });
            setAds(adsList);
            calculateStats(adsList);
        } catch (error) {
            console.error('Error fetching ads:', error);
        }
    };

    const calculateStats = (adsList) => {
        const totalViews = adsList.reduce((acc, ad) => acc + (ad.impressions || 0), 0);
        const clicks = adsList.reduce((acc, ad) => acc + (ad.clicks || 0), 0);
        const conversions = adsList.filter(ad => ad.converted).length;
        const revenue = adsList.reduce((acc, ad) => acc + (ad.revenue || 0), 0);
        const ctr = totalViews > 0 ? (clicks / totalViews) * 100 : 0;

        setStats({ totalViews, clicks, conversions, revenue, ctr });
    };

    // ─────────────────────────────────────────────────────────────────────────
    // AD MANAGEMENT
    // ─────────────────────────────────────────────────────────────────────────

    const handleOpenAddAd = () => {
        setDialogMode('add');
        setSelectedAd(null);
        setFormData({
            title: '',
            type: 'image',
            content: '',
            link: '',
            placement: 'sidebar',
            isActive: true,
            impressions: 0,
            clicks: 0
        });
        setOpenAdDialog(true);
    };

    const handleOpenEditAd = (ad) => {
        setDialogMode('edit');
        setSelectedAd(ad);
        setFormData({
            title: ad.title || '',
            type: ad.type || 'image',
            content: ad.content || '',
            link: ad.link || '',
            placement: ad.placement || 'sidebar',
            isActive: ad.isActive !== undefined ? ad.isActive : true,
            impressions: ad.impressions || 0,
            clicks: ad.clicks || 0
        });
        setOpenAdDialog(true);
    };

    const handleSaveAd = async () => {
        try {
            if (!formData.title || !formData.content) {
                toast.error('Title and content are required');
                return;
            }

            const adData = {
                ...formData,
                sponsorshipId: id,
                updatedAt: serverTimestamp(),
                updatedBy: user?.uid
            };

            if (dialogMode === 'add') {
                adData.createdAt = serverTimestamp();
                adData.createdBy = user?.uid;
                await addDoc(collection(db, 'ads'), adData);
                toast.success('Ad created successfully!');
            } else {
                await updateDoc(doc(db, 'ads', selectedAd.id), adData);
                toast.success('Ad updated successfully!');
            }

            setOpenAdDialog(false);
            fetchAds();
        } catch (error) {
            console.error('Error saving ad:', error);
            toast.error('Failed to save ad');
        }
    };

    const handleDeleteAd = async (adId) => {
        if (window.confirm('Are you sure you want to delete this ad?')) {
            try {
                await deleteDoc(doc(db, 'ads', adId));
                toast.success('Ad deleted successfully!');
                fetchAds();
            } catch (error) {
                console.error('Error deleting ad:', error);
                toast.error('Failed to delete ad');
            }
        }
    };

    const handleToggleAdStatus = async (adId, currentStatus) => {
        try {
            await updateDoc(doc(db, 'ads', adId), {
                isActive: !currentStatus,
                updatedAt: serverTimestamp()
            });
            toast.success(`Ad ${!currentStatus ? 'activated' : 'deactivated'} successfully!`);
            fetchAds();
        } catch (error) {
            console.error('Error toggling ad status:', error);
            toast.error('Failed to update ad status');
        }
    };

    const getAdTypeIcon = (type) => {
        switch(type) {
            case 'image': return <ImageIcon />;
            case 'video': return <VideocamIcon />;
            case 'slider': return <SlideshowIcon />;
            default: return <ImageIcon />;
        }
    };

    const getAdTypeColor = (type) => {
        switch(type) {
            case 'image': return palette.teal;
            case 'video': return palette.purple;
            case 'slider': return palette.rust;
            default: return palette.inkSoft;
        }
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return 'N/A';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    // ─────────────────────────────────────────────────────────────────────────
    // STATS DATA
    // ─────────────────────────────────────────────────────────────────────────

    const statsData = [
        {
            title: 'Total Views',
            value: stats.totalViews,
            icon: <VisibilityIcon  key="config-809-0-2" />,
            color: palette.rust,
            gradient: `linear-gradient(135deg, ${palette.rust}, ${palette.rustDeep})`,
            subtitle: 'Impressions',
            change: '+12%',
            trend: 'up'
        },
        {
            title: 'Clicks',
            value: stats.clicks,
            icon: <TrendingUpIcon  key="config-819-1-2" />,
            color: palette.pine,
            gradient: `linear-gradient(135deg, ${palette.pine}, ${palette.pineSoft})`,
            subtitle: 'Click-through',
            change: '+8%',
            trend: 'up'
        },
        {
            title: 'CTR',
            value: `${stats.ctr.toFixed(1)}%`,
            icon: <SpeedIcon  key="config-829-2-2" />,
            color: palette.teal,
            gradient: `linear-gradient(135deg, ${palette.teal}, ${palette.tealLight})`,
            subtitle: 'Click-through rate',
            change: '+5%',
            trend: 'up'
        },
        {
            title: 'Revenue',
            value: `$${stats.revenue}`,
            icon: <MoneyIcon  key="config-839-3-2" />,
            color: palette.gold,
            gradient: `linear-gradient(135deg, ${palette.gold}, ${palette.goldLight})`,
            subtitle: 'Generated',
            change: '+15%',
            trend: 'up'
        },
    ];

    const tabs = [
        { label: 'Overview', icon: <StorefrontIcon  key="config-852-0-1" />, color: palette.rust },
        { label: 'Ads Management', icon: <ImageIcon  key="config-853-1-1" />, color: palette.teal },
        { label: 'Analytics', icon: <AnalyticsIcon  key="config-854-2-1" />, color: palette.purple }
    ];

    // ─────────────────────────────────────────────────────────────────────────
    // RENDER
    // ─────────────────────────────────────────────────────────────────────────

    if (loading) {
        return (
            <Box sx={canvasSx}>
                <style>{FONT_IMPORT}</style>
                <Container maxWidth={false} sx={{ py: 4, px: { xs: 2, sm: 3, md: 4, lg: 6 }, position: 'relative', zIndex: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                        <motion.div
                            animate={{ 
                                rotate: 360,
                                scale: [1, 1.1, 1],
                            }}
                            transition={{ 
                                rotate: { duration: 2, repeat: Infinity, ease: 'linear' },
                                scale: { duration: 1.5, repeat: Infinity, ease: 'easeInOut' }
                            }}
                        >
                            <CircularProgress size={60} sx={{ color: palette.rust }} />
                        </motion.div>
                    </Box>
                </Container>
            </Box>
        );
    }

    if (!sponsorship) {
        return (
            <Box sx={canvasSx}>
                <style>{FONT_IMPORT}</style>
                <Container maxWidth={false} sx={{ py: 8, px: { xs: 2, sm: 3, md: 4, lg: 6 }, position: 'relative', zIndex: 1 }}>
                    <GlassCard accent={palette.rust}>
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <Typography variant="h6" sx={{ fontFamily: fontDisplay, color: palette.ink }}>
                                Sponsorship not found
                            </Typography>
                            <Button 
                                onClick={() => navigate('/sponsorships')} 
                                sx={{ mt: 2, borderRadius: '12px', textTransform: 'none', fontWeight: 600 }}
                            >
                                Back to Sponsorships
                            </Button>
                        </Box>
                    </GlassCard>
                </Container>
            </Box>
        );
    }

    return (
        <Box sx={canvasSx} ref={pageRef}>
            <style>{FONT_IMPORT}</style>
            <FloatingOrbs />

            <Container maxWidth={false} disableGutters sx={{ py: { xs: 3, md: 4 }, px: { xs: 2, sm: 3, md: 4, lg: 6 }, position: 'relative', zIndex: 1, width: '100%' }}>
                
                {/* ────────────────────────────────────────────────────────────
                    HEADER
                    ──────────────────────────────────────────────────────────── */}
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                    style={{ opacity: headerOpacity }}
                >
                    <Box sx={{ mb: 4 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                                    <IconButton 
                                        onClick={() => navigate('/sponsorships')} 
                                        sx={{ 
                                            color: palette.inkLight,
                                            bgcolor: palette.paper,
                                            backdropFilter: 'blur(8px)',
                                            border: `1px solid ${palette.hairline}`,
                                            '&:hover': { 
                                                bgcolor: palette.paper,
                                                borderColor: palette.rust,
                                                boxShadow: `0 4px 16px ${rgba(palette.rust, 0.15)}`
                                            }
                                        }}
                                    >
                                        <ArrowBackIcon />
                                    </IconButton>
                                </motion.div>
                                <Box>
                                    <Typography sx={{ fontFamily: fontBody, fontSize: '0.75rem', color: palette.inkLight, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>
                                        Sponsorship Details
                                    </Typography>
                                    <Typography variant="h4" sx={{ fontFamily: fontDisplay, fontWeight: 700, color: palette.ink }}>
                                        {company?.companyName || 'Sponsorship'}
                                    </Typography>
                                    <Typography sx={{ color: palette.inkSoft, mt: 0.5 }}>
                                        {sponsorship.packageType?.toUpperCase()} Package • ${sponsorship.amount} {sponsorship.currency}
                                    </Typography>
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                                <Chip
                                    icon={<LiveIcon sx={{ fontSize: 11, color: sponsorship.isActive ? `${palette.pine} !important` : `${palette.inkMuted} !important` }} />}
                                    label={sponsorship.isActive ? 'Active' : 'Inactive'}
                                    sx={{
                                        bgcolor: sponsorship.isActive ? rgba(palette.pine, 0.08) : rgba(palette.inkMuted, 0.08),
                                        color: sponsorship.isActive ? palette.pine : palette.inkMuted,
                                        fontWeight: 700,
                                        fontSize: '0.65rem',
                                        height: 30,
                                        borderRadius: '16px',
                                        border: `1px solid ${sponsorship.isActive ? rgba(palette.pine, 0.12) : rgba(palette.inkMuted, 0.12)}`,
                                        backdropFilter: 'blur(4px)',
                                    }}
                                />
                                <Chip
                                    icon={sponsorship.paymentStatus === 'paid' ? <CheckCircleIcon sx={{ fontSize: 14 }} /> : <ScheduleIcon sx={{ fontSize: 14 }} />}
                                    label={sponsorship.paymentStatus || 'Pending'}
                                    sx={{
                                        bgcolor: sponsorship.paymentStatus === 'paid' ? rgba(palette.pine, 0.08) : rgba(palette.gold, 0.08),
                                        color: sponsorship.paymentStatus === 'paid' ? palette.pine : palette.gold,
                                        fontWeight: 700,
                                        fontSize: '0.65rem',
                                        height: 30,
                                        borderRadius: '16px',
                                        border: `1px solid ${sponsorship.paymentStatus === 'paid' ? rgba(palette.pine, 0.12) : rgba(palette.gold, 0.12)}`,
                                        backdropFilter: 'blur(4px)',
                                    }}
                                />
                                <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}>
                                    <Button
                                        variant="contained"
                                        disableElevation
                                        startIcon={<AddIcon />}
                                        onClick={handleOpenAddAd}
                                        sx={{
                                            borderRadius: '12px',
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            px: 2.75,
                                            py: 1.1,
                                            background: `linear-gradient(135deg, ${palette.rust}, ${palette.rustDeep})`,
                                            boxShadow: `0 6px 16px -8px rgba(199,91,46,0.45)`,
                                            '&:hover': {
                                                boxShadow: `0 8px 24px -8px rgba(199,91,46,0.55)`,
                                                transform: 'translateY(-2px)',
                                            }
                                        }}
                                    >
                                        Create Ad
                                    </Button>
                                </motion.div>
                            </Box>
                        </Box>
                        <Divider sx={{ mt: 3, borderColor: palette.hairline }} />
                    </Box>
                </motion.div>

                {/* ────────────────────────────────────────────────────────────
                    STATS CARDS
                    ──────────────────────────────────────────────────────────── */}
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr', md: '1fr 1fr 1fr', lg: 'repeat(4, 1fr)' },
                        gap: 2.5,
                        width: '100%',
                        mb: 4,
                    }}
                >
                    {statsData.map((stat, index) => (
                        <GlassStatCard
                            key={stat.title}
                            title={stat.title}
                            value={stat.value}
                            icon={stat.icon}
                            color={stat.color}
                            gradient={stat.gradient}
                            delay={index * 0.05}
                            subtitle={stat.subtitle}
                            change={stat.change}
                            trend={stat.trend}
                        />
                    ))}
                </Box>

                {/* ────────────────────────────────────────────────────────────
                    MAIN GLASS CARD WITH TABS
                    ──────────────────────────────────────────────────────────── */}
                <GlassCard accent={palette.rust} delay={0.1} hover={false} elevation="lg" sx={{ p: 0 }}>
                    <Box sx={{ borderBottom: `1px solid ${palette.hairline}`, px: { xs: 1, sm: 2 }, pt: 1, overflowX: 'auto' }}>
                        <Tabs
                            value={tabValue}
                            onChange={(e, v) => setTabValue(v)}
                            variant="scrollable"
                            scrollButtons="auto"
                            sx={{
                                minHeight: 56,
                                '& .MuiTab-root': {
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    fontSize: { xs: '0.7rem', sm: '0.8rem' },
                                    py: 1.5,
                                    minHeight: 48,
                                    minWidth: 'auto',
                                    px: { xs: 1.5, sm: 2.5 },
                                    borderRadius: '10px 10px 0 0',
                                    transition: 'all 0.3s ease',
                                    color: palette.inkLight,
                                    fontFamily: fontBody,
                                    '&:hover': {
                                        color: palette.ink,
                                        bgcolor: 'rgba(26,21,18,0.02)',
                                    },
                                    '&.Mui-selected': {
                                        color: palette.ink,
                                    }
                                },
                                '& .MuiTabs-indicator': {
                                    height: 3,
                                    borderRadius: '3px',
                                    backgroundColor: tabs[tabValue]?.color || palette.rust,
                                }
                            }}
                        >
                            {tabs.map((tab, index) => (
                                <Tab
                                    key={index}
                                    icon={React.cloneElement(tab.icon, {
                                        sx: { fontSize: { xs: 16, sm: 18 }, color: tabValue === index ? tab.color : palette.inkLight }
                                    })}
                                    label={tab.label}
                                    iconPosition="start"
                                />
                            ))}
                        </Tabs>
                    </Box>

                    <Box sx={{ p: { xs: 2, sm: 3 } }}>
                        {/* ═══════════════════════════════════════════════════
                            TAB 0: OVERVIEW
                            ═══════════════════════════════════════════════════ */}
                        {tabValue === 0 && (
                            <Box>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={6}>
                                        <SectionHeader 
                                            icon={<StorefrontIcon sx={{ fontSize: 20 }} />}
                                            title="Sponsorship Information"
                                            subtitle="Package details and status"
                                            color={palette.rust}
                                        />
                                        <GlassCard accent={palette.rust} hover={false} elevation="sm">
                                            <Stack spacing={2}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1, borderBottom: `1px solid ${palette.hairline}` }}>
                                                    <Typography variant="body2" color={palette.inkLight}>Package</Typography>
                                                    <Chip 
                                                        label={sponsorship.packageType?.toUpperCase() || 'Premium'} 
                                                        size="small" 
                                                        sx={{ 
                                                            fontWeight: 700,
                                                            bgcolor: sponsorship.packageType === 'diamond' ? `${palette.teal}20` :
                                                                    sponsorship.packageType === 'platinum' ? `${palette.inkMuted}20` :
                                                                    sponsorship.packageType === 'gold' ? `${palette.gold}20` :
                                                                    `${palette.rust}20`,
                                                            color: sponsorship.packageType === 'diamond' ? palette.teal :
                                                                   sponsorship.packageType === 'platinum' ? palette.inkSoft :
                                                                   sponsorship.packageType === 'gold' ? palette.gold :
                                                                   palette.rust,
                                                            backdropFilter: 'blur(4px)',
                                                        }}
                                                    />
                                                </Box>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1, borderBottom: `1px solid ${palette.hairline}` }}>
                                                    <Typography variant="body2" color={palette.inkLight}>Amount</Typography>
                                                    <Typography variant="body2" fontWeight={600}>
                                                        ${sponsorship.amount} {sponsorship.currency}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1, borderBottom: `1px solid ${palette.hairline}` }}>
                                                    <Typography variant="body2" color={palette.inkLight}>Payment Status</Typography>
                                                    <Chip 
                                                        label={sponsorship.paymentStatus || 'Pending'} 
                                                        size="small" 
                                                        sx={{
                                                            fontWeight: 700,
                                                            bgcolor: sponsorship.paymentStatus === 'paid' ? rgba(palette.pine, 0.12) : rgba(palette.gold, 0.12),
                                                            color: sponsorship.paymentStatus === 'paid' ? palette.pine : palette.gold,
                                                            backdropFilter: 'blur(4px)',
                                                        }}
                                                    />
                                                </Box>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1, borderBottom: `1px solid ${palette.hairline}` }}>
                                                    <Typography variant="body2" color={palette.inkLight}>Status</Typography>
                                                    <Chip 
                                                        label={sponsorship.isActive ? 'Active' : 'Inactive'} 
                                                        size="small" 
                                                        sx={{
                                                            fontWeight: 700,
                                                            bgcolor: sponsorship.isActive ? rgba(palette.pine, 0.12) : rgba(palette.inkMuted, 0.12),
                                                            color: sponsorship.isActive ? palette.pine : palette.inkMuted,
                                                            backdropFilter: 'blur(4px)',
                                                        }}
                                                    />
                                                </Box>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
                                                    <Typography variant="body2" color={palette.inkLight}>Period</Typography>
                                                    <Typography variant="body2">
                                                        {formatDate(sponsorship.startDate)} - {formatDate(sponsorship.endDate)}
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                        </GlassCard>
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <SectionHeader 
                                            icon={<BusinessIcon sx={{ fontSize: 20 }} />}
                                            title="Company Information"
                                            subtitle="Sponsor company details"
                                            color={palette.purple}
                                        />
                                        <GlassCard accent={palette.purple} hover={false} elevation="sm">
                                            {company ? (
                                                <Stack spacing={2}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                        <Avatar 
                                                            sx={{ 
                                                                bgcolor: rgba(palette.rust, 0.12), 
                                                                color: palette.rust, 
                                                                width: 56, 
                                                                height: 56,
                                                                fontSize: '1.2rem',
                                                                fontWeight: 700,
                                                                border: `2px solid ${rgba(palette.rust, 0.1)}`
                                                            }}
                                                        >
                                                            {company.companyName?.charAt(0) || 'C'}
                                                        </Avatar>
                                                        <Box>
                                                            <Typography variant="subtitle1" fontWeight={600} sx={{ color: palette.ink }}>
                                                                {company.companyName}
                                                            </Typography>
                                                            <Typography variant="caption" sx={{ color: palette.inkLight }}>
                                                                {company.companyType || 'Company'}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                    <Divider sx={{ borderColor: palette.hairline }} />
                                                    {company.headquarters && (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <LocationIcon sx={{ fontSize: 18, color: palette.inkLight }} />
                                                            <Typography variant="body2" sx={{ color: palette.inkSoft }}>{company.headquarters}</Typography>
                                                        </Box>
                                                    )}
                                                    {company.website && (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <LanguageIcon sx={{ fontSize: 18, color: palette.inkLight }} />
                                                            <Typography variant="body2" sx={{ color: palette.inkSoft }}>{company.website}</Typography>
                                                        </Box>
                                                    )}
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <BusinessIcon sx={{ fontSize: 18, color: palette.inkLight }} />
                                                        <Typography variant="body2" sx={{ color: palette.inkSoft }}>{company.companyType || 'N/A'}</Typography>
                                                    </Box>
                                                    {company.isVerified && (
                                                        <Chip 
                                                            icon={<VerifiedIcon sx={{ fontSize: 14 }} />}
                                                            label="Verified Company" 
                                                            size="small" 
                                                            sx={{
                                                                fontWeight: 700,
                                                                bgcolor: rgba(palette.pine, 0.12),
                                                                color: palette.pine,
                                                                backdropFilter: 'blur(4px)',
                                                            }}
                                                        />
                                                    )}
                                                </Stack>
                                            ) : (
                                                <Typography variant="body2" color={palette.inkLight} textAlign="center" py={3}>
                                                    Company information not available
                                                </Typography>
                                            )}
                                        </GlassCard>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <SectionHeader 
                                            icon={<StarIcon sx={{ fontSize: 20 }} />}
                                            title="Package Features"
                                            subtitle="Benefits included in this package"
                                            color={palette.gold}
                                        />
                                        <GlassCard accent={palette.gold} hover={false} elevation="sm">
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                {sponsorship.features?.map((feature, index) => (
                                                    <Chip 
                                                        key={index} 
                                                        label={feature} 
                                                        icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                                                        sx={{ 
                                                            borderRadius: '8px',
                                                            bgcolor: rgba(palette.pine, 0.06),
                                                            color: palette.pine,
                                                            fontWeight: 500,
                                                            backdropFilter: 'blur(4px)',
                                                        }}
                                                    />
                                                ))}
                                                {(!sponsorship.features || sponsorship.features.length === 0) && (
                                                    <Typography variant="body2" color={palette.inkLight}>
                                                        No features listed for this package
                                                    </Typography>
                                                )}
                                            </Box>
                                        </GlassCard>
                                    </Grid>
                                </Grid>
                            </Box>
                        )}

                        {/* ═══════════════════════════════════════════════════
                            TAB 1: ADS MANAGEMENT
                            ═══════════════════════════════════════════════════ */}
                        {tabValue === 1 && (
                            <Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                                    <SectionHeader 
                                        icon={<ImageIcon sx={{ fontSize: 20 }} />}
                                        title="Ad Management"
                                        subtitle="Create and manage advertising campaigns"
                                        color={palette.teal}
                                    />
                                    <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}>
                                        <Button
                                            variant="contained"
                                            disableElevation
                                            startIcon={<AddIcon />}
                                            onClick={handleOpenAddAd}
                                            sx={{
                                                borderRadius: '12px',
                                                textTransform: 'none',
                                                fontWeight: 600,
                                                px: 3,
                                                py: 1.1,
                                                background: `linear-gradient(135deg, ${palette.rust}, ${palette.rustDeep})`,
                                                boxShadow: `0 6px 16px -8px rgba(199,91,46,0.45)`,
                                                '&:hover': {
                                                    boxShadow: `0 8px 24px -8px rgba(199,91,46,0.55)`,
                                                    transform: 'translateY(-2px)',
                                                }
                                            }}
                                        >
                                            Create Ad
                                        </Button>
                                    </motion.div>
                                </Box>

                                {ads.length === 0 ? (
                                    <GlassCard accent={palette.inkLight} hover={false} elevation="md">
                                        <Box sx={{ textAlign: 'center', py: 6 }}>
                                            <ImageIcon sx={{ fontSize: 56, color: palette.inkLight, opacity: 0.3, mb: 2 }} />
                                            <Typography sx={{ fontFamily: fontDisplay, fontWeight: 600, fontSize: '1.2rem', color: palette.ink }}>
                                                No ads created yet
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: palette.inkSoft, mt: 0.5, mb: 3 }}>
                                                Create your first ad to start promoting this sponsorship
                                            </Typography>
                                            <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}>
                                                <Button
                                                    variant="contained"
                                                    disableElevation
                                                    startIcon={<AddIcon />}
                                                    onClick={handleOpenAddAd}
                                                    sx={{
                                                        borderRadius: '12px',
                                                        textTransform: 'none',
                                                        fontWeight: 600,
                                                        px: 3,
                                                        py: 1.1,
                                                        background: `linear-gradient(135deg, ${palette.rust}, ${palette.rustDeep})`,
                                                        boxShadow: `0 6px 16px -8px rgba(199,91,46,0.45)`,
                                                        '&:hover': {
                                                            boxShadow: `0 8px 24px -8px rgba(199,91,46,0.55)`,
                                                            transform: 'translateY(-2px)',
                                                        }
                                                    }}
                                                >
                                                    Create Ad
                                                </Button>
                                            </motion.div>
                                        </Box>
                                    </GlassCard>
                                ) : (
                                    <Grid container spacing={3}>
                                        {ads.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((ad, index) => (
                                            <Grid item xs={12} sm={6} lg={4} key={ad.id}>
                                                <motion.div
                                                    initial={{ opacity: 0, y: 20 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ duration: 0.3, delay: index * 0.05 }}
                                                >
                                                    <GlassCard 
                                                        accent={getAdTypeColor(ad.type)} 
                                                        hover={true} 
                                                        elevation="sm"
                                                    >
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <Avatar sx={{ 
                                                                    bgcolor: rgba(getAdTypeColor(ad.type), 0.12), 
                                                                    color: getAdTypeColor(ad.type), 
                                                                    width: 40, 
                                                                    height: 40 
                                                                }}>
                                                                    {getAdTypeIcon(ad.type)}
                                                                </Avatar>
                                                                <Box>
                                                                    <Typography variant="subtitle2" fontWeight={600} sx={{ color: palette.ink }}>
                                                                        {ad.title}
                                                                    </Typography>
                                                                    <Typography variant="caption" sx={{ color: palette.inkLight }}>
                                                                        {ad.type} • {ad.placement}
                                                                    </Typography>
                                                                </Box>
                                                            </Box>
                                                            <Chip 
                                                                label={ad.isActive ? 'Active' : 'Inactive'} 
                                                                size="small" 
                                                                sx={{
                                                                    fontWeight: 700,
                                                                    bgcolor: ad.isActive ? rgba(palette.pine, 0.12) : rgba(palette.inkMuted, 0.12),
                                                                    color: ad.isActive ? palette.pine : palette.inkMuted,
                                                                    backdropFilter: 'blur(4px)',
                                                                }}
                                                            />
                                                        </Box>

                                                        <Box sx={{ mt: 2, p: 2, bgcolor: palette.canvasAlt, borderRadius: 2, minHeight: 50 }}>
                                                            <Typography variant="body2" color={palette.inkSoft} sx={{ wordBreak: 'break-all' }}>
                                                                {ad.content?.substring(0, 60)}...
                                                            </Typography>
                                                        </Box>

                                                        <Box sx={{ display: 'flex', gap: 3, mt: 2 }}>
                                                            <Box>
                                                                <Typography variant="caption" sx={{ color: palette.inkLight }}>Impressions</Typography>
                                                                <Typography variant="body2" fontWeight={600} sx={{ color: palette.ink }}>{ad.impressions || 0}</Typography>
                                                            </Box>
                                                            <Box>
                                                                <Typography variant="caption" sx={{ color: palette.inkLight }}>Clicks</Typography>
                                                                <Typography variant="body2" fontWeight={600} sx={{ color: palette.ink }}>{ad.clicks || 0}</Typography>
                                                            </Box>
                                                            <Box>
                                                                <Typography variant="caption" sx={{ color: palette.inkLight }}>CTR</Typography>
                                                                <Typography variant="body2" fontWeight={600} sx={{ color: palette.ink }}>
                                                                    {ad.impressions > 0 ? ((ad.clicks / ad.impressions) * 100).toFixed(1) : 0}%
                                                                </Typography>
                                                            </Box>
                                                        </Box>

                                                        <Divider sx={{ my: 2, borderColor: palette.hairline }} />

                                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                            <Tooltip title="Edit">
                                                                <IconButton 
                                                                    size="small" 
                                                                    onClick={() => handleOpenEditAd(ad)}
                                                                    sx={{ color: palette.gold, '&:hover': { bgcolor: `${palette.gold}12` } }}
                                                                >
                                                                    <EditIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title={ad.isActive ? 'Deactivate' : 'Activate'}>
                                                                <IconButton 
                                                                    size="small" 
                                                                    onClick={() => handleToggleAdStatus(ad.id, ad.isActive)}
                                                                    sx={{ color: ad.isActive ? palette.warning : palette.pine, '&:hover': { bgcolor: `${ad.isActive ? palette.warning : palette.pine}12` } }}
                                                                >
                                                                    {ad.isActive ? <CancelIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Delete">
                                                                <IconButton 
                                                                    size="small" 
                                                                    onClick={() => handleDeleteAd(ad.id)} 
                                                                    sx={{ color: palette.danger, '&:hover': { bgcolor: `${palette.danger}12` } }}
                                                                >
                                                                    <DeleteIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Box>
                                                    </GlassCard>
                                                </motion.div>
                                            </Grid>
                                        ))}
                                    </Grid>
                                )}
                                {ads.length > 0 && (
                                    <TablePagination
                                        rowsPerPageOptions={[3, 6, 9]}
                                        component="div"
                                        count={ads.length}
                                        rowsPerPage={rowsPerPage}
                                        page={page}
                                        onPageChange={handleChangePage}
                                        onRowsPerPageChange={handleChangeRowsPerPage}
                                        sx={{ 
                                            borderTop: `1px solid ${palette.hairline}`, 
                                            mt: 2, 
                                            pt: 1,
                                            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                                fontFamily: fontBody,
                                                color: palette.inkLight
                                            }
                                        }}
                                    />
                                )}
                            </Box>
                        )}

                        {/* ═══════════════════════════════════════════════════
                            TAB 2: ANALYTICS
                            ═══════════════════════════════════════════════════ */}
                        {tabValue === 2 && (
                            <Box>
                                <SectionHeader 
                                    icon={<AnalyticsIcon sx={{ fontSize: 20 }} />}
                                    title="Sponsorship Analytics"
                                    subtitle="Performance metrics and insights"
                                    color={palette.purple}
                                />
                                <Divider sx={{ mb: 3, borderColor: palette.hairline }} />

                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={4}>
                                        <GlassStatCard
                                            title="Click-through Rate"
                                            value={`${stats.ctr.toFixed(1)}%`}
                                            icon={<TrendingUpIcon />}
                                            color={palette.rust}
                                            gradient={`linear-gradient(135deg, ${palette.rust}, ${palette.rustDeep})`}
                                            subtitle="Overall CTR"
                                            change={stats.ctr > 0 ? '+5%' : '0%'}
                                            trend="up"
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <GlassStatCard
                                            title="Conversion Rate"
                                            value={stats.clicks > 0 ? `${((stats.conversions / stats.clicks) * 100).toFixed(1)}%` : '0%'}
                                            icon={<CheckCircleIcon />}
                                            color={palette.pine}
                                            gradient={`linear-gradient(135deg, ${palette.pine}, ${palette.pineSoft})`}
                                            subtitle="Conversions per click"
                                            change={stats.conversions > 0 ? '+3%' : '0%'}
                                            trend="up"
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <GlassStatCard
                                            title="Avg. Revenue per Ad"
                                            value={ads.length > 0 ? `$${(stats.revenue / ads.length).toFixed(2)}` : '$0'}
                                            icon={<MoneyIcon />}
                                            color={palette.gold}
                                            gradient={`linear-gradient(135deg, ${palette.gold}, ${palette.goldLight})`}
                                            subtitle="Per ad"
                                            change={stats.revenue > 0 ? '+8%' : '0%'}
                                            trend="up"
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <GlassCard accent={palette.teal} hover={false} elevation="md">
                                            <Typography variant="subtitle2" fontWeight={600} sx={{ color: palette.ink, mb: 2 }}>
                                                Ad Performance Overview
                                            </Typography>
                                            <TableContainer>
                                                <Table>
                                                    <TableHead>
                                                        <TableRow sx={{ bgcolor: palette.canvasAlt }}>
                                                            <TableCell sx={{ fontWeight: 700, color: palette.ink, fontFamily: fontBody }}>Ad Name</TableCell>
                                                            <TableCell align="center" sx={{ fontWeight: 700, color: palette.ink, fontFamily: fontBody }}>Type</TableCell>
                                                            <TableCell align="center" sx={{ fontWeight: 700, color: palette.ink, fontFamily: fontBody }}>Impressions</TableCell>
                                                            <TableCell align="center" sx={{ fontWeight: 700, color: palette.ink, fontFamily: fontBody }}>Clicks</TableCell>
                                                            <TableCell align="center" sx={{ fontWeight: 700, color: palette.ink, fontFamily: fontBody }}>CTR</TableCell>
                                                            <TableCell align="right" sx={{ fontWeight: 700, color: palette.ink, fontFamily: fontBody }}>Revenue</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {ads.map((ad) => {
                                                            const ctr = ad.impressions > 0 ? ((ad.clicks / ad.impressions) * 100).toFixed(1) : 0;
                                                            return (
                                                                <TableRow key={ad.id}>
                                                                    <TableCell>
                                                                        <Typography variant="body2" fontWeight={500} sx={{ color: palette.ink }}>
                                                                            {ad.title}
                                                                        </Typography>
                                                                    </TableCell>
                                                                    <TableCell align="center">
                                                                        <Chip 
                                                                            label={ad.type} 
                                                                            size="small" 
                                                                            sx={{ 
                                                                                textTransform: 'capitalize',
                                                                                fontWeight: 600,
                                                                                bgcolor: rgba(getAdTypeColor(ad.type), 0.12),
                                                                                color: getAdTypeColor(ad.type),
                                                                                backdropFilter: 'blur(4px)',
                                                                            }}
                                                                        />
                                                                    </TableCell>
                                                                    <TableCell align="center" sx={{ color: palette.ink }}>{ad.impressions || 0}</TableCell>
                                                                    <TableCell align="center" sx={{ color: palette.ink }}>{ad.clicks || 0}</TableCell>
                                                                    <TableCell align="center">
                                                                        <Chip 
                                                                            label={`${ctr}%`} 
                                                                            size="small" 
                                                                            sx={{
                                                                                fontWeight: 700,
                                                                                bgcolor: ctr > 5 ? rgba(palette.pine, 0.12) : ctr > 2 ? rgba(palette.gold, 0.12) : rgba(palette.inkMuted, 0.12),
                                                                                color: ctr > 5 ? palette.pine : ctr > 2 ? palette.gold : palette.inkMuted,
                                                                                backdropFilter: 'blur(4px)',
                                                                            }}
                                                                        />
                                                                    </TableCell>
                                                                    <TableCell align="right" sx={{ fontWeight: 600, color: palette.ink }}>
                                                                        ${ad.revenue || 0}
                                                                    </TableCell>
                                                                </TableRow>
                                                            );
                                                        })}
                                                        {ads.length === 0 && (
                                                            <TableRow>
                                                                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                                                    <Typography variant="body2" color={palette.inkLight}>
                                                                        No ads data available
                                                                    </Typography>
                                                                </TableCell>
                                                            </TableRow>
                                                        )}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </GlassCard>
                                    </Grid>
                                </Grid>
                            </Box>
                        )}
                    </Box>
                </GlassCard>

                {/* ────────────────────────────────────────────────────────────
                    AD DIALOG
                    ──────────────────────────────────────────────────────────── */}
                <Dialog
                    open={openAdDialog}
                    onClose={() => setOpenAdDialog(false)}
                    maxWidth="sm"
                    fullWidth
                    slotProps={{
                        paper: {
                            sx: {
                                ...liquidGlass(palette.teal, 0.08, 'lg'),
                                borderRadius: '24px',
                                overflow: 'hidden',
                                '&::after': sheenLayer(palette.teal),
                            }
                        }
                    }}
                >
                    <DialogTitle sx={{
                        borderBottom: `1px solid ${palette.hairline}`,
                        pb: 2,
                        fontFamily: fontDisplay,
                        fontWeight: 600,
                        color: palette.ink,
                        background: `linear-gradient(135deg, ${palette.canvasAlt}, ${palette.paper})`,
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            {dialogMode === 'add' ? (
                                <AddIcon sx={{ color: palette.rust }} />
                            ) : (
                                <EditIcon sx={{ color: palette.gold }} />
                            )}
                            {dialogMode === 'add' ? 'Create Ad' : 'Edit Ad'}
                        </Box>
                    </DialogTitle>
                    <DialogContent sx={{ pt: 3 }}>
                        <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <TextField
                                fullWidth
                                label="Ad Title"
                                value={formData.title}
                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                required
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '12px',
                                        backgroundColor: palette.paper,
                                        backdropFilter: 'blur(8px)',
                                        border: `1px solid ${palette.hairline}`,
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            borderColor: palette.rust,
                                            backgroundColor: palette.paper,
                                            boxShadow: `0 4px 12px ${palette.rust}15`,
                                        },
                                        '&.Mui-focused': {
                                            borderColor: palette.rust,
                                            backgroundColor: palette.paper,
                                            boxShadow: `0 4px 16px ${palette.rust}20`,
                                        }
                                    }
                                }}
                            />

                            <FormControl fullWidth>
                                <InputLabel sx={{ color: palette.inkSoft }}>Ad Type</InputLabel>
                                <Select
                                    value={formData.type}
                                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                                    label="Ad Type"
                                    sx={{
                                        borderRadius: '12px',
                                        backgroundColor: palette.paper,
                                        backdropFilter: 'blur(8px)',
                                        border: `1px solid ${palette.hairline}`,
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            borderColor: palette.rust,
                                            backgroundColor: palette.paper,
                                            boxShadow: `0 4px 12px ${palette.rust}15`,
                                        },
                                        '&.Mui-focused': {
                                            borderColor: palette.rust,
                                            backgroundColor: palette.paper,
                                            boxShadow: `0 4px 16px ${palette.rust}20`,
                                        }
                                    }}
                                >
                                    <MenuItem value="image">Image</MenuItem>
                                    <MenuItem value="video">Video</MenuItem>
                                    <MenuItem value="slider">Slider/Carousel</MenuItem>
                                </Select>
                            </FormControl>

                            <TextField
                                fullWidth
                                label="Content URL"
                                value={formData.content}
                                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                                placeholder="https://example.com/ad-image.jpg"
                                required
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '12px',
                                        backgroundColor: palette.paper,
                                        backdropFilter: 'blur(8px)',
                                        border: `1px solid ${palette.hairline}`,
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            borderColor: palette.rust,
                                            backgroundColor: palette.paper,
                                            boxShadow: `0 4px 12px ${palette.rust}15`,
                                        },
                                        '&.Mui-focused': {
                                            borderColor: palette.rust,
                                            backgroundColor: palette.paper,
                                            boxShadow: `0 4px 16px ${palette.rust}20`,
                                        }
                                    }
                                }}
                                helperText="Image URL, Video URL, or Slider embed code"
                            />

                            <TextField
                                fullWidth
                                label="Link URL"
                                value={formData.link}
                                onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                                placeholder="https://example.com/company-profile"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '12px',
                                        backgroundColor: palette.paper,
                                        backdropFilter: 'blur(8px)',
                                        border: `1px solid ${palette.hairline}`,
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            borderColor: palette.rust,
                                            backgroundColor: palette.paper,
                                            boxShadow: `0 4px 12px ${palette.rust}15`,
                                        },
                                        '&.Mui-focused': {
                                            borderColor: palette.rust,
                                            backgroundColor: palette.paper,
                                            boxShadow: `0 4px 16px ${palette.rust}20`,
                                        }
                                    }
                                }}
                                helperText="URL to open when ad is clicked"
                            />

                            <FormControl fullWidth>
                                <InputLabel sx={{ color: palette.inkSoft }}>Placement</InputLabel>
                                <Select
                                    value={formData.placement}
                                    onChange={(e) => setFormData({ ...formData, placement: e.target.value })}
                                    label="Placement"
                                    sx={{
                                        borderRadius: '12px',
                                        backgroundColor: palette.paper,
                                        backdropFilter: 'blur(8px)',
                                        border: `1px solid ${palette.hairline}`,
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            borderColor: palette.rust,
                                            backgroundColor: palette.paper,
                                            boxShadow: `0 4px 12px ${palette.rust}15`,
                                        },
                                        '&.Mui-focused': {
                                            borderColor: palette.rust,
                                            backgroundColor: palette.paper,
                                            boxShadow: `0 4px 16px ${palette.rust}20`,
                                        }
                                    }}
                                >
                                    <MenuItem value="sidebar">Sidebar</MenuItem>
                                    <MenuItem value="banner">Banner</MenuItem>
                                    <MenuItem value="popup">Popup</MenuItem>
                                    <MenuItem value="inline">Inline</MenuItem>
                                    <MenuItem value="footer">Footer</MenuItem>
                                </Select>
                            </FormControl>

                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.isActive}
                                        onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                                        sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': {
                                                color: palette.pine,
                                            },
                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                backgroundColor: palette.pine,
                                            },
                                        }}
                                    />
                                }
                                label="Active"
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ borderTop: `1px solid ${palette.hairline}`, p: 2.5, gap: 1 }}>
                        <Button
                            onClick={() => setOpenAdDialog(false)}
                            sx={{ color: palette.inkSoft, fontWeight: 600, textTransform: 'none' }}
                        >
                            Cancel
                        </Button>
                        <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                            <Button 
                                variant="contained" 
                                disableElevation
                                onClick={handleSaveAd} 
                                startIcon={dialogMode === 'add' ? <AddIcon /> : <SaveIcon />}
                                sx={{
                                    borderRadius: '12px',
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    px: 3,
                                    py: 1.1,
                                    background: `linear-gradient(135deg, ${palette.rust}, ${palette.rustDeep})`,
                                    boxShadow: `0 6px 16px -8px rgba(199,91,46,0.45)`,
                                    '&:hover': {
                                        boxShadow: `0 8px 24px -8px rgba(199,91,46,0.55)`,
                                        transform: 'translateY(-2px)',
                                    }
                                }}
                            >
                                {dialogMode === 'add' ? 'Create Ad' : 'Save Changes'}
                            </Button>
                        </motion.div>
                    </DialogActions>
                </Dialog>

                {/* ────────────────────────────────────────────────────────────
                    SNACKBAR
                    ──────────────────────────────────────────────────────────── */}
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                >
                    <Alert
                        severity={snackbar.severity}
                        onClose={handleCloseSnackbar}
                        variant="filled"
                        sx={{ 
                            borderRadius: 2,
                            bgcolor: snackbar.severity === 'success' ? palette.pine : palette.danger,
                        }}
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Container>

            {/* ────────────────────────────────────────────────────────────
                GLOBAL STYLES
                ──────────────────────────────────────────────────────────── */}
            <style>
                {`
                    @keyframes pulse {
                        0%, 100% { opacity: 1; transform: scale(1); }
                        50% { opacity: 0.5; transform: scale(0.8); }
                    }
                    @keyframes grainDrift {
                        0% { transform: translate(0, 0); }
                        20% { transform: translate(-2%, 2%); }
                        40% { transform: translate(2%, -1%); }
                        60% { transform: translate(-1%, -2%); }
                        80% { transform: translate(1%, 1%); }
                        100% { transform: translate(0, 0); }
                    }
                    * {
                        scrollbar-width: thin;
                        scrollbar-color: ${palette.hairline} transparent;
                    }
                    ::-webkit-scrollbar {
                        width: 6px;
                        height: 6px;
                    }
                    ::-webkit-scrollbar-track {
                        background: transparent;
                    }
                    ::-webkit-scrollbar-thumb {
                        background: ${palette.hairline};
                        border-radius: 3px;
                    }
                    ::-webkit-scrollbar-thumb:hover {
                        background: ${palette.hairlineStrong};
                    }
                    .MuiTabs-scrollButtons {
                        color: ${palette.inkLight};
                    }
                    .MuiTabs-scrollButtons:hover {
                        color: ${palette.rust};
                    }
                `}
            </style>
        </Box>
    );
};

export default SponsorDetails;